import { Tooltip } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridOffIcon from '@mui/icons-material/GridOff';
import TerminalIcon from '@mui/icons-material/Terminal';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloudIcon from '@mui/icons-material/Cloud';
import MapIcon from '@mui/icons-material/Map';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import IosShareIcon from '@mui/icons-material/IosShare';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';

import { JxIconButton, JxLineEdit } from '../../../JxControls';
import { tooltipSxElevated } from '../../../components/shared/jernyTooltipStyling.js';

/**
 * UmlCanvasToolbarButtons — pure painter for the toolbar's button row (Add
 *
 * @param {number} zoomPercent
 * @param {Function} onAddPackage
 * @param {Function} onAddClass
 * @param {Function} onAddRemarks
 * @param {Function} onAddIconShape
 * @param {boolean} snapToGrid
 * @param {Function} onToggleSnap
 * @param {number} gridSize
 * @param {Function} onGridSizeChange
 * @param {Function} onOutputLog
 * @param {boolean} worldMapOpen
 * @param {Function} onToggleWorldMap
 * @param {boolean} hiddenItemsOpen
 * @param {Function} onToggleHiddenItems
 * @param {boolean} hideInterfaces
 * @param {Function} onToggleHideInterfaces
 * @param {Function} onFixLines
 * @param {Function} onAutoLayout
 * @param {Function} onUpdatePreview
 * @param {Function} onExportImage
 * @param {boolean} isPackageSelected
 * @param {Function} onCopyPackage
 * @param {boolean} pkgLibViewerOpen
 * @param {Function} onTogglePkgLibViewer
 */
export default function UmlCanvasToolbarButtons({
    zoomPercent,
    onAddPackage,
    onAddClass,
    onAddRemarks,
    onAddIconShape,
    snapToGrid,
    onToggleSnap,
    gridSize,
    onGridSizeChange,
    onOutputLog,
    worldMapOpen,
    onToggleWorldMap,
    hiddenItemsOpen,
    onToggleHiddenItems,
    hideInterfaces,
    onToggleHideInterfaces,
    onFixLines,
    onAutoLayout,
    onUpdatePreview,
    onExportImage,
    isPackageSelected,
    onCopyPackage,
    pkgLibViewerOpen,
    onTogglePkgLibViewer,
}) {
    return (
        <div className="uct-button-row">
            <span className="uct-zoom-label">{zoomPercent}%</span>

            <Tooltip title="Add Package" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<FolderIcon sx={{ fontSize: 18 }} />} onClick={onAddPackage} />
                </span>
            </Tooltip>
            <Tooltip title="Add Class" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<ViewAgendaIcon sx={{ fontSize: 18 }} />} onClick={onAddClass} />
                </span>
            </Tooltip>
            <Tooltip title="Add Remarks" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<StickyNote2Icon sx={{ fontSize: 18 }} />} onClick={onAddRemarks} />
                </span>
            </Tooltip>
            <Tooltip title="Add Icon Shape" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<CloudIcon sx={{ fontSize: 18 }} />} onClick={onAddIconShape} />
                </span>
            </Tooltip>

            <Tooltip title={snapToGrid ? 'Grid Snap: On' : 'Grid Snap: Off'} componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton
                        icon={snapToGrid ? <GridOnIcon sx={{ fontSize: 18 }} /> : <GridOffIcon sx={{ fontSize: 18 }} />}
                        onClick={onToggleSnap}
                    />
                </span>
            </Tooltip>
            <div className="uct-grid-size-input">
                <JxLineEdit
                    id="uct_gridSize"
                    value={String(gridSize)}
                    onChange={onGridSizeChange}
                    placeholder="Grid"
                    disabled={!snapToGrid}
                />
            </div>

            <Tooltip title="Output Log" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<TerminalIcon sx={{ fontSize: 18 }} />} onClick={onOutputLog} />
                </span>
            </Tooltip>

            <Tooltip title={worldMapOpen ? 'Hide World Map' : 'Show World Map'} componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<MapIcon sx={{ fontSize: 18 }} />} onClick={onToggleWorldMap} />
                </span>
            </Tooltip>

            <Tooltip title={hiddenItemsOpen ? 'Hide Hidden Items' : 'Show Hidden Items'} componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<Inventory2Icon sx={{ fontSize: 18 }} />} onClick={onToggleHiddenItems} />
                </span>
            </Tooltip>

            <Tooltip title={hideInterfaces ? 'Show Interfaces' : 'Hide Interfaces'} componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton
                        icon={hideInterfaces ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                        onClick={onToggleHideInterfaces}
                    />
                </span>
            </Tooltip>

            <Tooltip title="Fix Lines" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<AutoFixHighIcon sx={{ fontSize: 18 }} />} onClick={onFixLines} />
                </span>
            </Tooltip>

            <Tooltip title="Auto Layout" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<AccountTreeIcon sx={{ fontSize: 18 }} />} onClick={onAutoLayout} />
                </span>
            </Tooltip>

            <Tooltip title="Update Preview" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<PhotoCameraIcon sx={{ fontSize: 18 }} />} onClick={onUpdatePreview} />
                </span>
            </Tooltip>

            <Tooltip title="Export Image" componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<IosShareIcon sx={{ fontSize: 18 }} />} onClick={onExportImage} />
                </span>
            </Tooltip>

            <Tooltip title={isPackageSelected ? 'Copy Package (C)' : 'Select a package first'} componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<ContentCopyIcon sx={{ fontSize: 18 }} />} onClick={onCopyPackage} disabled={!isPackageSelected} />
                </span>
            </Tooltip>

            <Tooltip title={pkgLibViewerOpen ? 'Hide Package Library' : 'Show Package Library'} componentsProps={tooltipSxElevated}>
                <span>
                    <JxIconButton icon={<CollectionsBookmarkIcon sx={{ fontSize: 18 }} />} onClick={onTogglePkgLibViewer} />
                </span>
            </Tooltip>
        </div>
    );
}
